import { EXERCISES } from '@/data/exercises';

export interface ExerciseData {
  exercise_name: string;
  sets: number;
  reps: number;
  weight_kg?: number;
}

export interface SingleWorkoutPlan {
  type: 'single_workout';
  exercises: ExerciseData[];
}

export interface WeeklyWorkoutDay {
  day: string;
  workout_name: string;
  exercises: ExerciseData[];
}

export interface WeeklyWorkoutPlan {
  type: 'weekly_plan';
  days: WeeklyWorkoutDay[];
}

export interface RunningInterval {
  repeat?: number;
  distance_km?: number;
  duration_minutes?: number;
  pace?: string;
  rest_minutes?: number;
}

export interface RunningWorkoutPlan {
  type: 'running_workout';
  title?: string;
  warmup_minutes?: number;
  intervals: RunningInterval[];
  cooldown_minutes?: number;
  notes?: string;
  total_distance_km?: number;
  estimated_duration_minutes?: number;
  pacing_guide?: string[];
}

export interface RunningWorkoutCollection {
  type: 'running_workouts';
  workouts: RunningWorkoutPlan[];
}

export type WorkoutPlan = SingleWorkoutPlan | WeeklyWorkoutPlan | RunningWorkoutPlan | RunningWorkoutCollection;

// Function to find matching exercise from the database
export const findMatchingExercise = (exerciseName: string) => {
  const normalizedName = exerciseName.toLowerCase().trim();
  
  // First try exact match
  let exercise = EXERCISES.find(ex => ex.name.toLowerCase() === normalizedName);
  
  if (exercise) return exercise;
  
  // Try partial match
  exercise = EXERCISES.find(ex => 
    ex.name.toLowerCase().includes(normalizedName) || 
    normalizedName.includes(ex.name.toLowerCase())
  );
  
  if (exercise) return exercise;
  
  // Try alternative names and common variations
  const exerciseMap: { [key: string]: string } = {
    'pushup': 'Push-Up',
    'pushups': 'Push-Up',
    'push up': 'Push-Up',
    'pullup': 'Pull-Up',
    'pullups': 'Pull-Up',
    'pull up': 'Pull-Up',
    'situp': 'Sit-Up',
    'situps': 'Sit-Up',
    'sit up': 'Sit-Up',
    'bicep curl': 'Dumbbell Curl',
    'biceps curl': 'Dumbbell Curl',
    'shoulder press': 'Dumbbell Shoulder Press',
    'military press': 'Overhead Press',
    'chest fly': 'Dumbbell Chest Fly',
    'chest flye': 'Dumbbell Chest Fly',
    'lat pulldown': 'Lat Pulldown With Pronated Grip',
    'barbell row': 'Barbell Row',
    'bent over row': 'Barbell Row',
    'tricep extension': 'Dumbbell Standing Triceps Extension',
    'triceps extension': 'Dumbbell Standing Triceps Extension',
    'leg press': 'Leg Press',
    'calf raise': 'Standing Calf Raise',
    'leg curl': 'Lying Leg Curl',
    'leg extension': 'Leg Extension',
    'hip thrust': 'Hip Thrust',
    'glute bridge': 'Glute Bridge'
  };
  
  const mappedName = exerciseMap[normalizedName];
  if (mappedName) {
    exercise = EXERCISES.find(ex => ex.name === mappedName);
    if (exercise) return exercise;
  }
  
  return null;
};

/** Fast guard — skip expensive JSON/prose parsing on plain chat messages. */
export const messageLikelyContainsWorkoutPlan = (message: string): boolean => {
  if (!message || message.length < 24) return false;
  if (message.includes('```json')) return true;
  if (/"type"\s*:\s*"(single_workout|running_workout|running_workouts|weekly_plan)"/.test(message)) {
    return true;
  }
  if (
    /\b(warm-?up|cool-?down|main\s+set|tempo|interval|long\s+run)\b/i.test(message) &&
    /\b(min|km|pace|zone\s*[1-5]|bpm)\b/i.test(message)
  ) {
    return true;
  }
  return false;
};

/** Build importable running_workout JSON when Arnold describes a run but omits the code block. */
export const inferRunningWorkoutFromProse = (message: string): RunningWorkoutPlan | null => {
  const text = message || '';
  const looksLikeRun =
    /\b(warm-?up|cool-?down|main\s+set|tempo|interval|long\s+run)\b/i.test(text) &&
    /\b(min|km|pace|zone\s*[1-5]|bpm)\b/i.test(text);
  if (!looksLikeRun) return null;

  const titleMatch = text.match(/^##\s*([^\n]+)/m) || text.match(/##\s*([^\n(]+)/);
  const title = titleMatch?.[1]?.trim().replace(/\*\*/g, '');

  const warmupM = text.match(/warm-?up[:\s*]*(\d+)\s*min/i);
  const cooldownM = text.match(/cool-?down[:\s*]*(\d+)\s*min/i);

  const intervals: RunningInterval[] = [];
  const mainSection = text.match(/\*\*main\s+set:?\*\*([\s\S]*?)(?=\*\*cool-?down|\*\*pacing|\*\*total|$)/i);
  const block = mainSection?.[1] || text;

  const addUnique = (it: RunningInterval) => {
    const key = JSON.stringify(it);
    if (!intervals.some((x) => JSON.stringify(x) === key)) intervals.push(it);
  };

  for (const m of block.matchAll(
    /(\d+)\s*min(?:utes?)?\s+at\s+(?:zone\s*)?Z?([1-5])(?:\s*\([^)]*(\d+)\s*[-–]\s*(\d+)\s*bpm[^)]*\))?/gi,
  )) {
    const dur = parseInt(m[1], 10);
    const zone = `Z${m[2]}`;
    const bpm = m[3] && m[4] ? `${m[3]}-${m[4]} bpm` : undefined;
    addUnique({ duration_minutes: dur, pace: bpm ? `${zone} (${bpm})` : zone });
  }

  for (const m of block.matchAll(/(\d+(?:\.\d+)?)\s*km\s+at\s+([\d:]+\s*\/?\s*km?|[^,\n]+)/gi)) {
    addUnique({ distance_km: parseFloat(m[1]), pace: m[2].trim() });
  }

  for (const m of block.matchAll(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*km/gi)) {
    addUnique({ repeat: parseInt(m[1], 10), distance_km: parseFloat(m[2]) });
  }

  const distM = text.match(/(?:total\s+distance|approximately)\s*([\d.]+)\s*(?:[-–]\s*([\d.]+))?\s*km/i);
  const durM = text.match(/estimated\s+duration[:\s*]*(\d+)\s*min/i);

  if (!warmupM && intervals.length === 0 && !cooldownM) return null;

  return {
    type: 'running_workout',
    title: title || undefined,
    warmup_minutes: warmupM ? parseInt(warmupM[1], 10) : undefined,
    intervals,
    cooldown_minutes: cooldownM ? parseInt(cooldownM[1], 10) : undefined,
    total_distance_km: distM
      ? distM[2]
        ? (parseFloat(distM[1]) + parseFloat(distM[2])) / 2
        : parseFloat(distM[1])
      : undefined,
    estimated_duration_minutes: durM ? parseInt(durM[1], 10) : undefined,
  };
};

// Function to parse JSON from Arnold's response
export const parseWorkoutPlanFromMessage = (message: string): WorkoutPlan | null => {
  try {
    if (!messageLikelyContainsWorkoutPlan(message)) return null;

    // Prefer the LAST JSON code block in the message (most likely the actual plan)
    const blocks = Array.from(message.matchAll(/```json\s*([\s\S]*?)\s*```/g));
    let jsonString = '';

    if (blocks.length > 0) {
      jsonString = blocks[blocks.length - 1][1];
    } else if (/"type"\s*:\s*"(single_workout|running_workout|running_workouts|weekly_plan)"/.test(message)) {
      // Only attempt greedy JSON extraction when the message clearly embeds a plan object.
      const jsonMatch = message.match(/\{[\s\S]*\}/);
      if (jsonMatch && jsonMatch[0].length <= 120_000) {
        jsonString = jsonMatch[0];
      }
    }

    if (!jsonString) return inferRunningWorkoutFromProse(message);

    const parsed = JSON.parse(jsonString);

    // Helpers to coerce numbers safely from mixed formats (e.g., "4", "4x8", "8-10", "80 kg")
    // ALWAYS picks the HIGHEST value from ranges (e.g., "10-12" returns 12, "90-95kg" returns 95)
    const toInt = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.round(Math.max(...nums));
      }
      return undefined;
    };

    const toFloat = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.replace(',', '.').match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n)).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.max(...nums);
      }
      return undefined;
    };

    // Validate the structure
    if (parsed.type === 'single_workout' && Array.isArray(parsed.exercises)) {
      // Normalize and validate exercises
      const normalized = parsed.exercises
        .map((ex: any) => {
          const rawWeight = ex.weight_kg ?? ex.weight ?? ex.weightKg;
          return {
            exercise_name: String(ex.exercise_name || '').trim(),
            sets: toInt(ex.sets),
            reps: toInt(ex.reps),
            weight_kg: rawWeight !== undefined ? toFloat(rawWeight) : undefined,
          };
        })
        .filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number');

      if (normalized.length > 0) {
        return {
          type: 'single_workout',
          exercises: normalized,
        };
      }
    } else if (parsed.type === 'weekly_plan' && Array.isArray(parsed.days)) {
      // Normalize weekly plan
      const validDays = parsed.days
        .map((day: any) => ({
          day: String(day.day || '').trim(),
          workout_name: String(day.workout_name || '').trim(),
          exercises: Array.isArray(day.exercises)
            ? day.exercises
                .map((ex: any) => ({
                  exercise_name: String(ex.exercise_name || '').trim(),
                  sets: toInt(ex.sets),
                  reps: toInt(ex.reps),
                  weight_kg: ex.weight_kg !== undefined ? toFloat(ex.weight_kg) : (ex.weight !== undefined ? toFloat(ex.weight) : (ex.weightKg !== undefined ? toFloat(ex.weightKg) : undefined)),
                }))
                .filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number')
            : [],
        }))
        .filter((day: any) => day.day && day.workout_name && day.exercises.length > 0);

      if (validDays.length > 0) {
        return {
          type: 'weekly_plan',
          days: validDays,
        };
      }
    } else if (parsed.type === 'running_workout' || parsed.type === 'running_workouts') {
      const toIntSimple = (v: any) => {
        if (typeof v === 'number') return Math.round(v);
        if (typeof v === 'string') { const m = v.match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.round(Math.max(...m.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n)))); }
        return undefined;
      };
      const toFloatSimple = (v: any) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') { const m = v.replace(',', '.').match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.max(...m.map(n => parseFloat(n)).filter(n => !isNaN(n))); }
        return undefined;
      };
      const normalizeRunningWorkout = (raw: any): RunningWorkoutPlan | null => {
        const intervals = Array.isArray(raw.intervals) ? raw.intervals.map((it: any) => ({
        repeat: toIntSimple(it.repeat) || 1,
        distance_km: toFloatSimple(it.distance_km),
        duration_minutes: toFloatSimple(it.duration_minutes),
        pace: typeof it.pace === 'string' ? it.pace : undefined,
        rest_minutes: toFloatSimple(it.rest_minutes),
        })) : [];
        const plan: RunningWorkoutPlan = {
          type: 'running_workout',
          title: String(raw.title || '').trim() || undefined,
          warmup_minutes: toIntSimple(raw.warmup_minutes),
          intervals,
          cooldown_minutes: toIntSimple(raw.cooldown_minutes),
          notes: typeof raw.notes === 'string' ? raw.notes : undefined,
          total_distance_km: toFloatSimple(raw.total_distance_km),
          estimated_duration_minutes: toFloatSimple(raw.estimated_duration_minutes),
          pacing_guide: Array.isArray(raw.pacing_guide)
            ? raw.pacing_guide.map((item: any) => String(item || '').trim()).filter(Boolean)
            : undefined,
        };
        return plan.title || intervals.length > 0 || plan.notes ? plan : null;
      };

      if (parsed.type === 'running_workouts') {
        const workouts = Array.isArray(parsed.workouts)
          ? parsed.workouts.map(normalizeRunningWorkout).filter(Boolean)
          : [];
        if (workouts.length > 0) return { type: 'running_workouts', workouts } as RunningWorkoutCollection;
        return null;
      }

      return normalizeRunningWorkout(parsed);
    }

    return inferRunningWorkoutFromProse(message);
  } catch (error) {
    console.error('Error parsing workout plan from message:', error);
    return inferRunningWorkoutFromProse(message);
  }
};

export type RunningCoachFormatOptions = {
  /** When set, append Z1–Z5 bpm ranges so the live coach can guide by heart rate. */
  hrZones?: Array<{ id: string; lowerBpm: number; upperBpm: number }>;
  healthConnected?: boolean;
};

const zoneBpm = (zones: RunningCoachFormatOptions['hrZones'], id: string): string => {
  const z = zones?.find((x) => x.id === id);
  return z ? `${z.lowerBpm}-${z.upperBpm} bpm` : '';
};

export const formatRunningWorkoutForCoach = (
  plan: RunningWorkoutPlan,
  options?: RunningCoachFormatOptions,
): string => {
  const lines: string[] = [];
  const z2 = zoneBpm(options?.hrZones, 'z2');
  const z3 = zoneBpm(options?.hrZones, 'z3');
  const z4 = zoneBpm(options?.hrZones, 'z4');
  const z1 = zoneBpm(options?.hrZones, 'z1');
  const useHr = !!(options?.healthConnected && options?.hrZones?.length);

  if (plan.title) lines.push(plan.title);
  if (plan.warmup_minutes) {
    lines.push(
      useHr && z2
        ? `Warm-up: ${plan.warmup_minutes} minutes easy at zone 2 (${z2}).`
        : `Warm-up: ${plan.warmup_minutes} minutes easy.`,
    );
  }
  if (plan.intervals?.length) {
    lines.push('Main set:');
    plan.intervals.forEach((interval) => {
      const repeat = interval.repeat && interval.repeat > 1 ? `${interval.repeat} x ` : '';
      const work = interval.distance_km
        ? `${interval.distance_km} km`
        : interval.duration_minutes
          ? `${interval.duration_minutes} minutes`
          : 'run segment';
      const pace = interval.pace ? ` at ${interval.pace}` : '';
      const hrHint =
        useHr && /tempo|threshold|hard|interval|fast/i.test(`${interval.pace || ''}`)
          ? z4
            ? `, target zone 4 (${z4})`
            : z3
              ? `, target zone 3 (${z3})`
              : ''
          : useHr && z2
            ? `, target zone 2 (${z2})`
            : '';
      const rest = interval.rest_minutes
        ? ` with ${interval.rest_minutes} minutes easy recovery${useHr && z1 ? ` at zone 1 (${z1})` : ''}`
        : '';
      lines.push(`- ${repeat}${work}${pace}${hrHint}${rest}`);
    });
  }
  if (plan.cooldown_minutes) {
    lines.push(
      useHr && z1
        ? `Cool-down: ${plan.cooldown_minutes} minutes easy at zone 1 (${z1}).`
        : `Cool-down: ${plan.cooldown_minutes} minutes easy.`,
    );
  }
  if (plan.estimated_duration_minutes) {
    lines.push(`Estimated duration: ${plan.estimated_duration_minutes} minutes.`);
  }
  if (plan.pacing_guide?.length) {
    lines.push('Pacing guide:');
    plan.pacing_guide.forEach((item) => {
      let line = item;
      if (useHr && !/\bbpm\b/i.test(item) && /zone\s*2|z2|easy|aerobic|conversational/i.test(item) && z2) {
        line = `${item} (${z2})`;
      } else if (useHr && !/\bbpm\b/i.test(item) && /zone\s*3|z3|tempo/i.test(item) && z3) {
        line = `${item} (${z3})`;
      } else if (useHr && !/\bbpm\b/i.test(item) && /zone\s*4|z4|threshold|interval/i.test(item) && z4) {
        line = `${item} (${z4})`;
      }
      lines.push(`- ${line}`);
    });
  }
  if (plan.notes) lines.push(plan.notes);
  return lines.filter(Boolean).join('\n');
};

// Function to convert parsed workout plan to workout logger format
export const convertToWorkoutLoggerFormat = (plan: WorkoutPlan) => {
  if (plan.type === 'single_workout') {
    return plan.exercises.map(exercise => {
      const matchedExercise = findMatchingExercise(exercise.exercise_name);
      return {
        id: `imported-${Date.now()}-${Math.random()}`,
        name: matchedExercise?.name || exercise.exercise_name,
        sets: exercise.sets,
        reps: exercise.reps,
        weight: exercise.weight_kg || 0,
        muscleGroups: matchedExercise?.muscle_groups || [],
        equipment: matchedExercise?.equipment,
        matched: !!matchedExercise
      };
    });
  }
  
  return null; // Weekly plans need different handling
};

// Function to import single workout into workout logger
export const importSingleWorkout = (
  plan: SingleWorkoutPlan,
  onImport: (exercises: any[]) => void
) => {
  const exercises = convertToWorkoutLoggerFormat(plan);
  if (exercises) {
    onImport(exercises);
    return true;
  }
  return false;
};